/*
 * Venn Platform API 1.0.0 Operations for "userclient"
 * Automatically generated file - do not modify!
 */

import type {
  Account,
  AccountQuery,
  AppUser,
  DetailedAccount,
  PermissionGroup,
  PermissionGroupEntity,
  ResponseBodyString,
  TokenTypeEnum,
  TrialDetails,
  UserOrgRoleEnum,
  UserRoleEnum,
  UserWorkspace,
  WorkspaceInvitation,
} from '../types';
import type { OperationResult } from '../../utils';
import { buildQuery, buildRequestInit, extractError, retryGeneratedFetch } from '../../utils';

/** No summary or description available. Please update the summary or description field in the Openapi spec. */
export async function getAccountTrialDetails(
  accountId: number,
  signal?: AbortSignal,
): Promise<OperationResult<TrialDetails>> {
  const endpoint = `/api/userclient/account/${encodeURIComponent(accountId)}/trial`;
  const requestInit = buildRequestInit({ method: 'GET', signal });
  const response = await retryGeneratedFetch('GET', () => fetch(endpoint, requestInit));
  if (response.status >= 200 && response.status < 400) {
    const content: TrialDetails = (await response.json()) as TrialDetails;
    return {
      content,
      status: response.status,
      headers: {},
    };
  }
  throw await extractError(response);
}
/** No summary or description available. Please update the summary or description field in the Openapi spec. */
export async function getAllUserAccounts(signal?: AbortSignal): Promise<OperationResult<Account[]>> {
  const endpoint = '/api/userclient/account';
  const requestInit = buildRequestInit({ method: 'GET', signal });
  const response = await retryGeneratedFetch('GET', () => fetch(endpoint, requestInit));
  if (response.status >= 200 && response.status < 400) {
    const content: Account[] = (await response.json()) as Account[];
    return {
      content,
      status: response.status,
      headers: {},
    };
  }
  throw await extractError(response);
}
/** No summary or description available. Please update the summary or description field in the Openapi spec. */
export async function getAllUsers(
  includeInactive?: boolean,
  signal?: AbortSignal,
): Promise<OperationResult<AppUser[]>> {
  const endpoint = `/api/userclient/user${buildQuery({ includeInactive })}`;
  const requestInit = buildRequestInit({ method: 'GET', signal });
  const response = await retryGeneratedFetch('GET', () => fetch(endpoint, requestInit));
  if (response.status >= 200 && response.status < 400) {
    const content: AppUser[] = (await response.json()) as AppUser[];
    return {
      content,
      status: response.status,
      headers: {},
    };
  }
  throw await extractError(response);
}
/** No summary or description available. Please update the summary or description field in the Openapi spec. */
export async function getAllWorkspaces(signal?: AbortSignal): Promise<OperationResult<UserWorkspace[]>> {
  const endpoint = '/api/userclient/org';
  const requestInit = buildRequestInit({ method: 'GET', signal });
  const response = await retryGeneratedFetch('GET', () => fetch(endpoint, requestInit));
  if (response.status >= 200 && response.status < 400) {
    const content: UserWorkspace[] = (await response.json()) as UserWorkspace[];
    return {
      content,
      status: response.status,
      headers: {},
    };
  }
  throw await extractError(response);
}
/** No summary or description available. Please update the summary or description field in the Openapi spec. */
export async function getEntityTiedToPermissionGroup(
  id: number,
  signal?: AbortSignal,
): Promise<OperationResult<PermissionGroupEntity>> {
  const endpoint = `/api/userclient/perm/${encodeURIComponent(id)}/entity`;
  const requestInit = buildRequestInit({ method: 'GET', signal });
  const response = await retryGeneratedFetch('GET', () => fetch(endpoint, requestInit));
  if (response.status >= 200 && response.status < 400) {
    const content: PermissionGroupEntity = (await response.json()) as PermissionGroupEntity;
    return {
      content,
      status: response.status,
      headers: {},
    };
  }
  throw await extractError(response);
}
/** No summary or description available. Please update the summary or description field in the Openapi spec. */
export async function getPermissionGroupForUser(
  userId: number,
  signal?: AbortSignal,
): Promise<OperationResult<PermissionGroup>> {
  const endpoint = `/api/userclient/perm/user/${encodeURIComponent(userId)}`;
  const requestInit = buildRequestInit({ method: 'GET', signal });
  const response = await retryGeneratedFetch('GET', () => fetch(endpoint, requestInit));
  if (response.status >= 200 && response.status < 400) {
    const content: PermissionGroup = (await response.json()) as PermissionGroup;
    return {
      content,
      status: response.status,
      headers: {},
    };
  }
  throw await extractError(response);
}
/** No summary or description available. Please update the summary or description field in the Openapi spec. */
export async function getPermissionGroupForWorkspace(
  workspaceId: number,
  signal?: AbortSignal,
): Promise<OperationResult<PermissionGroup>> {
  const endpoint = `/api/userclient/perm/org/${encodeURIComponent(workspaceId)}`;
  const requestInit = buildRequestInit({ method: 'GET', signal });
  const response = await retryGeneratedFetch('GET', () => fetch(endpoint, requestInit));
  if (response.status >= 200 && response.status < 400) {
    const content: PermissionGroup = (await response.json()) as PermissionGroup;
    return {
      content,
      status: response.status,
      headers: {},
    };
  }
  throw await extractError(response);
}
/** No summary or description available. Please update the summary or description field in the Openapi spec. */
export async function getPermissionGroupsUserBelongsTo(
  userId: number,
  workspaceId?: number,
  signal?: AbortSignal,
): Promise<OperationResult<number[]>> {
  const endpoint = `/api/userclient/perm/user/${encodeURIComponent(userId)}/groups${buildQuery({ workspaceId })}`;
  const requestInit = buildRequestInit({ method: 'GET', signal });
  const response = await retryGeneratedFetch('GET', () => fetch(endpoint, requestInit));
  if (response.status >= 200 && response.status < 400) {
    const content: number[] = (await response.json()) as number[];
    return {
      content,
      status: response.status,
      headers: {},
    };
  }
  throw await extractError(response);
}
/** No summary or description available. Please update the summary or description field in the Openapi spec. */
export async function getTokenClaims(
  token: string,
  signal?: AbortSignal,
): Promise<OperationResult<{ [key: string]: any }>> {
  const endpoint = `/api/userclient/user/authkey/${encodeURIComponent(token)}`;
  const requestInit = buildRequestInit({ method: 'GET', signal });
  const response = await retryGeneratedFetch('GET', () => fetch(endpoint, requestInit));
  if (response.status >= 200 && response.status < 400) {
    const content: { [key: string]: any } = (await response.json()) as { [key: string]: any };
    return {
      content,
      status: response.status,
      headers: {},
    };
  }
  throw await extractError(response);
}
/** No summary or description available. Please update the summary or description field in the Openapi spec. */
export async function getUser(userId: number, signal?: AbortSignal): Promise<OperationResult<AppUser>> {
  const endpoint = `/api/userclient/user/${encodeURIComponent(userId)}`;
  const requestInit = buildRequestInit({ method: 'GET', signal });
  const response = await retryGeneratedFetch('GET', () => fetch(endpoint, requestInit));
  if (response.status >= 200 && response.status < 400) {
    const content: AppUser = (await response.json()) as AppUser;
    return {
      content,
      status: response.status,
      headers: {},
    };
  }
  throw await extractError(response);
}
/** No summary or description available. Please update the summary or description field in the Openapi spec. */
export async function getUserAccountWorkspaces(id: number, signal?: AbortSignal): Promise<OperationResult<number[]>> {
  const endpoint = `/api/userclient/account/${encodeURIComponent(id)}/org`;
  const requestInit = buildRequestInit({ method: 'GET', signal });
  const response = await retryGeneratedFetch('GET', () => fetch(endpoint, requestInit));
  if (response.status >= 200 && response.status < 400) {
    const content: number[] = (await response.json()) as number[];
    return {
      content,
      status: response.status,
      headers: {},
    };
  }
  throw await extractError(response);
}
/** No summary or description available. Please update the summary or description field in the Openapi spec. */
export async function getUserByEmail(email: string, signal?: AbortSignal): Promise<OperationResult<AppUser>> {
  const endpoint = `/api/userclient/user/email/${encodeURIComponent(email)}`;
  const requestInit = buildRequestInit({ method: 'GET', signal });
  const response = await retryGeneratedFetch('GET', () => fetch(endpoint, requestInit));
  if (response.status >= 200 && response.status < 400) {
    const content: AppUser = (await response.json()) as AppUser;
    return {
      content,
      status: response.status,
      headers: {},
    };
  }
  throw await extractError(response);
}
/** No summary or description available. Please update the summary or description field in the Openapi spec. */
export async function getUserRoles(userId: number, signal?: AbortSignal): Promise<OperationResult<UserRoleEnum[]>> {
  const endpoint = `/api/userclient/user/${encodeURIComponent(userId)}/roles`;
  const requestInit = buildRequestInit({ method: 'GET', signal });
  const response = await retryGeneratedFetch('GET', () => fetch(endpoint, requestInit));
  if (response.status >= 200 && response.status < 400) {
    const content: UserRoleEnum[] = (await response.json()) as UserRoleEnum[];
    return {
      content,
      status: response.status,
      headers: {},
    };
  }
  throw await extractError(response);
}
/** No summary or description available. Please update the summary or description field in the Openapi spec. */
export async function getUserSystemGroups(userId: number, signal?: AbortSignal): Promise<OperationResult<string[]>> {
  const endpoint = `/api/userclient/user/${encodeURIComponent(userId)}/system-groups`;
  const requestInit = buildRequestInit({ method: 'GET', signal });
  const response = await retryGeneratedFetch('GET', () => fetch(endpoint, requestInit));
  if (response.status >= 200 && response.status < 400) {
    const content: string[] = (await response.json()) as string[];
    return {
      content,
      status: response.status,
      headers: {},
    };
  }
  throw await extractError(response);
}
/** No summary or description available. Please update the summary or description field in the Openapi spec. */
export async function getUserToken(
  userId?: number,
  workspaceId?: number,
  tokenType?: TokenTypeEnum,
  signal?: AbortSignal,
): Promise<OperationResult<ResponseBodyString>> {
  const endpoint = `/api/userclient/token${buildQuery({ userId, workspaceId, tokenType })}`;
  const requestInit = buildRequestInit({ method: 'GET', signal });
  const response = await retryGeneratedFetch('GET', () => fetch(endpoint, requestInit));
  if (response.status >= 200 && response.status < 400) {
    const content: ResponseBodyString = (await response.json()) as ResponseBodyString;
    return {
      content,
      status: response.status,
      headers: {},
    };
  }
  throw await extractError(response);
}
/** No summary or description available. Please update the summary or description field in the Openapi spec. */
export async function getUserWorkspace(
  workspaceId: number,
  signal?: AbortSignal,
): Promise<OperationResult<UserWorkspace>> {
  const endpoint = `/api/userclient/org/${encodeURIComponent(workspaceId)}`;
  const requestInit = buildRequestInit({ method: 'GET', signal });
  const response = await retryGeneratedFetch('GET', () => fetch(endpoint, requestInit));
  if (response.status >= 200 && response.status < 400) {
    const content: UserWorkspace = (await response.json()) as UserWorkspace;
    return {
      content,
      status: response.status,
      headers: {},
    };
  }
  throw await extractError(response);
}
/** No summary or description available. Please update the summary or description field in the Openapi spec. */
export async function getUserWorkspaceRole(
  userId: number,
  workspaceId: number,
  signal?: AbortSignal,
): Promise<OperationResult<UserOrgRoleEnum>> {
  const endpoint = `/api/userclient/user/${encodeURIComponent(userId)}/org/${encodeURIComponent(workspaceId)}/role`;
  const requestInit = buildRequestInit({ method: 'GET', signal });
  const response = await retryGeneratedFetch('GET', () => fetch(endpoint, requestInit));
  if (response.status >= 200 && response.status < 400) {
    const content: UserOrgRoleEnum = (await response.json()) as UserOrgRoleEnum;
    return {
      content,
      status: response.status,
      headers: {},
    };
  }
  throw await extractError(response);
}
/** No summary or description available. Please update the summary or description field in the Openapi spec. */
export async function getUserWorkspaces(userId: number, signal?: AbortSignal): Promise<OperationResult<number[]>> {
  const endpoint = `/api/userclient/user/${encodeURIComponent(userId)}/organizations`;
  const requestInit = buildRequestInit({ method: 'GET', signal });
  const response = await retryGeneratedFetch('GET', () => fetch(endpoint, requestInit));
  if (response.status >= 200 && response.status < 400) {
    const content: number[] = (await response.json()) as number[];
    return {
      content,
      status: response.status,
      headers: {},
    };
  }
  throw await extractError(response);
}
/** No summary or description available. Please update the summary or description field in the Openapi spec. */
export async function getUsers(body?: number[], signal?: AbortSignal): Promise<OperationResult<AppUser[]>> {
  const endpoint = '/api/userclient/users';
  const requestInit = buildRequestInit({
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
    signal,
  });
  const response = await retryGeneratedFetch('POST', () => fetch(endpoint, requestInit));
  if (response.status >= 200 && response.status < 400) {
    const content: AppUser[] = (await response.json()) as AppUser[];
    return {
      content,
      status: response.status,
      headers: {},
    };
  }
  throw await extractError(response);
}
/** No summary or description available. Please update the summary or description field in the Openapi spec. */
export async function getUsersInWorkspace(
  workspaceId: number,
  signal?: AbortSignal,
): Promise<OperationResult<number[]>> {
  const endpoint = `/api/userclient/org/${encodeURIComponent(workspaceId)}/member`;
  const requestInit = buildRequestInit({ method: 'GET', signal });
  const response = await retryGeneratedFetch('GET', () => fetch(endpoint, requestInit));
  if (response.status >= 200 && response.status < 400) {
    const content: number[] = (await response.json()) as number[];
    return {
      content,
      status: response.status,
      headers: {},
    };
  }
  throw await extractError(response);
}
/** No summary or description available. Please update the summary or description field in the Openapi spec. */
export async function getWorkspaceAccount(
  workspaceId: number,
  signal?: AbortSignal,
): Promise<OperationResult<DetailedAccount>> {
  const endpoint = `/api/userclient/org/${encodeURIComponent(workspaceId)}/account`;
  const requestInit = buildRequestInit({ method: 'GET', signal });
  const response = await retryGeneratedFetch('GET', () => fetch(endpoint, requestInit));
  if (response.status >= 200 && response.status < 400) {
    const content: DetailedAccount = (await response.json()) as DetailedAccount;
    return {
      content,
      status: response.status,
      headers: {},
    };
  }
  throw await extractError(response);
}
/** No summary or description available. Please update the summary or description field in the Openapi spec. */
export async function getWorkspacePendingInvites(
  workspaceId: number,
  signal?: AbortSignal,
): Promise<OperationResult<WorkspaceInvitation[]>> {
  const endpoint = `/api/userclient/org/${encodeURIComponent(workspaceId)}/invite`;
  const requestInit = buildRequestInit({ method: 'GET', signal });
  const response = await retryGeneratedFetch('GET', () => fetch(endpoint, requestInit));
  if (response.status >= 200 && response.status < 400) {
    const content: WorkspaceInvitation[] = (await response.json()) as WorkspaceInvitation[];
    return {
      content,
      status: response.status,
      headers: {},
    };
  }
  throw await extractError(response);
}
/** Search for accounts */
export async function searchAccountsWithQuery(
  body?: Partial<AccountQuery>,
  signal?: AbortSignal,
): Promise<OperationResult<Account[]>> {
  const endpoint = '/api/userclient/account/search';
  const requestInit = buildRequestInit({
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
    signal,
  });
  const response = await retryGeneratedFetch('POST', () => fetch(endpoint, requestInit));
  if (response.status >= 200 && response.status < 400) {
    const content: Account[] = (await response.json()) as Account[];
    return {
      content,
      status: response.status,
      headers: {},
    };
  }
  throw await extractError(response);
}
